import { useQuery } from '@tanstack/react-query';
import { useTranslation } from '../../../lib/i18n';
import { queryKeys } from '../../../lib/query-client';
import { fetchConnectors } from '../../../lib/connectors';
import { connectorDisplayName, connectorForSlug } from '../../../lib/skill-connector';
import { connectorSlugName } from '../../../lib/routine-groups';
import { type WebRoutine } from '../../../lib/routines';
import { ConnectorLogo } from '../connectors/ConnectorLogo';
import { Card, Typography } from '../ui';
import styles from './routines.legacy.module.css';

/**
 * A group's display name. The connectors payload spells an app the vendor's own way, so it wins
 * whenever it is there; it is absent both before the read lands and for a kit whose app the
 * connectors screen never lists (a Wix vertical is installed on the site, not connected), and the
 * slug titles the group in either case.
 *
 * A hook rather than a helper because the name needs the connectors query — and shared, so the
 * phone's group card and the desktop stream's heading can never title the same group differently.
 */
export function useRoutineGroupName(slug?: string): string {
  const { t } = useTranslation();
  const { data } = useQuery({ queryKey: queryKeys.connectors, queryFn: fetchConnectors });
  const connector = slug ? connectorForSlug(slug, data?.connectors) : null;
  const name = connector ? connectorDisplayName(connector) : connectorSlugName(slug);
  return name || t('routines.group.general');
}

/**
 * The DESKTOP stream's group heading (Figma 18352-340460): the group's name over its cards, and
 * nothing else — no logo, no count, no surface. The rail beside it already carries both, so the
 * heading only has to say which group the reader has scrolled into.
 */
export function RoutineGroupHeading({ slug }: { slug?: string }) {
  const name = useRoutineGroupName(slug);
  return (
    <Typography variant="h4" className={styles.streamHeading}>
      {name}
    </Typography>
  );
}

// One card per app a kit came from, holding that app's routines as rows — the Routines screen's grouping.
//
// The top level is the connector because that is the fact a reader can act on: it is the app the routines
// read, and the reason they exist at all. The SUB-groups a routine carries (`groupKey`) only order the
// rows here; their headings render on the connector's card in the conversation, where there is one
// connector and a heading cannot repeat.
//
// The last card has no connector: everything the user and the agent made themselves. It is deliberately
// last rather than first — a connector's card explains itself by its logo, and this one is the remainder.
export function RoutineConnectorCard({
  slug,
  children,
  count,
}: {
  slug?: string;
  count: number;
  children: React.ReactNode;
}) {
  const { data } = useQuery({ queryKey: queryKeys.connectors, queryFn: fetchConnectors });
  const connector = slug ? connectorForSlug(slug, data?.connectors) : null;
  const name = useRoutineGroupName(slug);

  // ONE card per group (Figma 18352-340166): the app's name and count at the top, a hairline under
  // it, and the group's routines as rows parted by the same hairline. It used to be a title card
  // with separate row cards beneath, inset to show they belonged to it — the single card says that
  // outright, and a phone gets the width back that the indent was spending.
  //
  // `disablePadding` because the head and each row pad themselves: a row is a press target and has
  // to reach the card's edges, or the gaps between rows are dead to the touch.
  return (
    <Card size="sm" disablePadding className={styles.groupCard} component="section" aria-label={name}>
      <div className={styles.groupHead}>
        {connector && <ConnectorLogo connector={connector} name={name} size={24} radius={6} glyphSize={14} />}
        <Typography variant="h4" className={styles.groupTitle}>{name}</Typography>
        <Typography variant="p3" color="secondary" className={styles.groupCount}>{String(count)}</Typography>
      </div>
      <div className={styles.groupRows}>{children}</div>
    </Card>
  );
}
